import React, { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { omit } from 'lodash';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CLA_SETTINGS_FLAG, CLAItem, CLAItemFormValues } from './types';

import {
  convertFlagsArrayToObjects,
  convertFlagsObjectToArray
} from '../../../../utils/misc';
import { generalSchema } from './utils';

import { CLA_YEAR_SETTINGS } from 'RestAPIs/endpoints';
import { useSaveResource } from 'RestAPIs/useSaveResource';

import { showSnackbar } from 'components/shared';
import EditPage from 'components/shared/CRUD/components/InnerPage/EditPage';
import useForceRefresh from 'components/shared/hooks/useForceRefresh';

import TabsAtClaSettings from './DetailView';

interface Props {
  toggleInnerLayout: () => void;
  refreshList: () => void;
  rowData: CLAItem;
  rows: CLAItem[];
}

const EditCLA: React.FC<Props> = ({
  toggleInnerLayout,
  refreshList,
  rows,
  rowData
}) => {
  const { t } = useTranslation('common');

  const [currentViewData, setCurrentViewData] = useState<CLAItemFormValues>();

  const [currentSchema, setCurrentSchema] = useState(generalSchema(t));
  const { timestamp: isEditable, refresh: setIsEditable } = useForceRefresh();

  const { mutateAsync: save } = useSaveResource({
    id: rowData.claYearId,
    url: CLA_YEAR_SETTINGS,
    invalidateQueryKey: 'claYears'
  });

  useEffect(() => {
    const formValues: CLAItemFormValues = {
      ...rowData,
      rowId: rowData.claYearId,
      flags: convertFlagsArrayToObjects(rowData.flags),
      workHoursBasedWeekdays: !!rowData.workHoursBasedWeekdays,
      plbValidity: rowData.flags.includes(CLA_SETTINGS_FLAG.PLB_5_YEARS)
        ? CLA_SETTINGS_FLAG.PLB_5_YEARS
        : rowData.flags.includes(CLA_SETTINGS_FLAG.PLB_10_YEARS)
        ? CLA_SETTINGS_FLAG.PLB_10_YEARS
        : ''
    };
    setCurrentViewData(formValues);
  }, [rowData]);

  const methods = useForm({
    defaultValues: currentSchema.cast(currentViewData),
    resolver: yupResolver(currentSchema),
    mode: 'onChange'
  });

  useEffect(() => {
    methods.reset({
      ...currentSchema.cast({
        ...currentViewData
      })
    });
  }, [currentViewData]);

  const onSubmit = async (data) => {
    await save(
      {
        ...omit(data, ['plbValidity']),
        flags: convertFlagsObjectToArray(data.flags),
        workHoursBasedWeekdays: data.workHoursBasedWeekdays ? 1 : 0
      },
      {
        onSuccess: () => {
          showSnackbar({ message: t('changesSaved') });
          setIsEditable();
          methods.reset(data);
          refreshList();
        },
        onError: () => {
          showSnackbar({
            message: t('somethingWentWrong'),
            severity: 'error'
          });
        }
      }
    );
  };

  return (
    <EditPage
      onCancel={() => methods.reset()}
      methods={methods}
      onSubmit={methods.handleSubmit(onSubmit)}
      toggleInnerLayout={toggleInnerLayout}
      currentView={currentViewData}
      setCurrentView={setCurrentViewData}
      views={rows}
      isEditable={isEditable}
      navigatorLabelGetter={['code', 'year']}
      navigatorValueGetter="claYearId"
      withTabs={true}
    >
      <TabsAtClaSettings setCurrentSchema={setCurrentSchema} />
    </EditPage>
  );
};

export default EditCLA;
